// ═══════════════════════════════════════════════════════════════════════════
// archivosSubibles — qué tipos de archivo acepta Storage, dicho UNA vez en el front
// ═══════════════════════════════════════════════════════════════════════════
//
// ESPEJO de `storage.rules` (`imagenSegura()`, `contentTypeSeguro()` y
// `mediaDelClubValida()`). La fuente de verdad son las rules: esto existe para
// que el rebote pase ANTES de subir y con un mensaje legible, en vez de un
// `storage/unauthorized` mudo después de esperar el upload. Si se toca una
// lista de allá, se toca la de acá en el mismo PR (el test de rules cubre el
// candado real; el de este archivo, que el espejo no se aflojó por su cuenta).
//
// Por qué NO hay `image/svg+xml` ni `text/html` (T6, 5-sep-2026): el
// contentType lo declara el cliente, y un SVG o un HTML con script servido
// desde el bucket es contenido activo (stored XSS / phishing bajo el origen de
// Storage). Antes había cinco uploaders con `accept="image/*"` o `text/*` que
// dejaban elegirlos, y dos rebotaban en silencio.

// `tamanoRazonable()` — el tope de los matches que usan contentTypeSeguro().
export const MAX_BYTES_SEGURO = 25 * 1024 * 1024

// `imagenSegura()` — un string es igualdad exacta; una RegExp, match completo.
export const IMAGENES_PERMITIDAS = Object.freeze([
  'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/heic', 'image/heif',
])

const OFFICE_OPENXML = /^application\/vnd\.openxmlformats-officedocument\..+$/

// `contentTypeSeguro()` — adjuntos del chat, reuniones, documentos internos,
// staging de debug, archivos de proyecto.
export const TIPOS_SEGUROS = Object.freeze([
  ...IMAGENES_PERMITIDAS,
  'application/pdf',
  'application/msword',
  OFFICE_OPENXML,
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'application/json',
  'text/plain',
  'text/csv',
  'text/markdown',
  /^audio\/(mpeg|mp4|wav|webm|ogg)$/,
  /^video\/(mp4|webm|quicktime)$/,
])

// `mediaDelClubValida()` — imágenes editoriales y material descargable del Club.
export const TIPOS_MEDIA_CLUB = Object.freeze([
  ...IMAGENES_PERMITIDAS,
  'application/pdf',
  OFFICE_OPENXML,
  'application/vnd.ms-excel',
  'text/csv',
  'application/zip',
])

// Para el `accept` del <input type="file">: extensiones (el picker del SO
// filtra por extensión mejor que por MIME) + los MIME exactos. Sin `image/*`
// ni `text/*`: esos comodines eran justamente la puerta del svg y del html.
const EXT_IMAGENES = '.png,.jpg,.jpeg,.gif,.webp,.heic,.heif'
export const ACCEPT_IMAGENES = `${EXT_IMAGENES},${IMAGENES_PERMITIDAS.join(',')}`
export const ACCEPT_SEGURO = [
  EXT_IMAGENES,
  // Sin `.m4a`: Chrome le pone `audio/x-m4a`, que las rules no admiten — el
  // picker no tiene que invitar a lo que después rebota.
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.json,.txt,.csv,.md,.mp3,.wav,.ogg,.mp4,.webm,.mov',
  ...TIPOS_SEGUROS.filter((t) => typeof t === 'string'),
].join(',')
export const ACCEPT_MEDIA_CLUB = [
  EXT_IMAGENES,
  '.pdf,.xls,.xlsx,.docx,.pptx,.csv,.zip',
  ...TIPOS_MEDIA_CLUB.filter((t) => typeof t === 'string'),
].join(',')

/** PURO: ¿este MIME está en la lista? Igualdad exacta o match completo de RegExp. */
export function tipoPermitido(type, permitidos = TIPOS_SEGUROS) {
  const t = String(type || '').toLowerCase()
  if (!t) return false
  return permitidos.some((p) => (p instanceof RegExp ? p.test(t) : p === t))
}

function esContenidoActivo(type) {
  const t = String(type || '').toLowerCase()
  return t === 'image/svg+xml' || t === 'text/html' || t === 'application/xhtml+xml'
}

/**
 * PURO: valida un File/Blob contra una allowlist y un tope (por default, el de
 * `tamanoRazonable()`; los paths con tope propio lo pasan explícito).
 * Devuelve `{ ok: true }` o `{ ok: false, error }` con un mensaje para mostrar
 * tal cual. El mensaje distingue el caso de seguridad (svg/html) del de
 * formato, porque "no permitido" a secas hace que la gente lo reintente.
 * @param {{type?:string,size?:number,name?:string}|null} file
 * @param {{permitidos?: ReadonlyArray<string|RegExp>, maxBytes?: number, descripcion?: string}} [opts]
 */
export function validarArchivoSubible(file, { permitidos = TIPOS_SEGUROS, maxBytes = MAX_BYTES_SEGURO, descripcion = 'PDF, Office, imágenes (PNG/JPG/WebP), texto, audio o video' } = {}) {
  if (!file) return { ok: false, error: 'No se eligió ningún archivo' }
  const nombre = file.name ? `"${file.name}"` : 'El archivo'
  if (esContenidoActivo(file.type)) {
    return { ok: false, error: `${nombre} es SVG/HTML: por seguridad no se aceptan (pueden ejecutar código). Convertilo a PNG o PDF.` }
  }
  if (!tipoPermitido(file.type, permitidos)) {
    const tipo = file.type ? ` (${file.type})` : ' (tipo desconocido)'
    return { ok: false, error: `${nombre}${tipo} no está entre los formatos permitidos: ${descripcion}.` }
  }
  if (typeof file.size === 'number' && file.size > maxBytes) {
    const mb = (file.size / (1024 * 1024)).toFixed(1)
    const tope = Math.round(maxBytes / (1024 * 1024))
    return { ok: false, error: `${nombre} pesa ${mb} MB — el máximo es ${tope} MB` }
  }
  return { ok: true }
}
